import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { userInitials } from '../devUser';
import { resizeImage } from '../util/image';
import './ProfileDialog.css';

export default function ProfileDialog({ onClose }) {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [image, setImage] = useState(user?.image || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const modalRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const node = modalRef.current;
    const prev = document.activeElement;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); } };
    node.addEventListener('keydown', onKey);
    node.querySelector('.pf-name input')?.focus();
    return () => { node.removeEventListener('keydown', onKey); if (prev?.focus) prev.focus(); };
  }, []);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('יש לבחור קובץ תמונה.'); return; }
    try { setImage(await resizeImage(file, 256)); setError(''); }
    catch { setError('טעינת התמונה נכשלה.'); }
  };

  async function save() {
    if (!name.trim()) { setError('יש להזין שם.'); return; }
    setError('');
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), image: image || '' });
      onClose();
    } catch (err) {
      setError(err.message || 'השמירה נכשלה.');
      setSaving(false);
    }
  }

  return (
    <div className="pf-backdrop" onMouseDown={onClose}>
      <div ref={modalRef} className="pf-modal" role="dialog" aria-modal="true" aria-label="הפרופיל שלי" tabIndex={-1} onMouseDown={(e) => e.stopPropagation()}>
        <header className="pf-head">
          <h2>הפרופיל שלי</h2>
          <button type="button" className="pf-x" onClick={onClose} aria-label="סגור"><i className="fi fi-rr-cross-small" /></button>
        </header>

        <div className="pf-body">
          {error && <div className="pf-error" role="alert"><i className="fi fi-rr-exclamation" /> {error}</div>}

          <div className="pf-photo-row">
            <div className="pf-photo">
              {image ? <img src={image} alt="" /> : <span>{userInitials(name)}</span>}
            </div>
            <div className="pf-photo-actions">
              <button type="button" className="pf-link" onClick={() => fileRef.current?.click()}>
                {image ? 'החלפת תמונה' : 'העלאת תמונה'}
              </button>
              {image && <button type="button" className="pf-link pf-link-muted" onClick={() => setImage('')}>הסרה</button>}
              <input ref={fileRef} type="file" accept="image/*" className="pf-file" onChange={onPick} />
            </div>
          </div>

          <label className="pf-field pf-name">
            <span>שם מלא</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="השם שלכם" />
          </label>
          <label className="pf-field">
            <span>אימייל</span>
            <input className="pf-ltr" type="text" value={user?.email || ''} disabled />
          </label>
        </div>

        <footer className="pf-foot">
          <button type="button" className="pf-btn pf-btn-ghost" onClick={onClose} disabled={saving}>ביטול</button>
          <button type="button" className="pf-btn pf-btn-primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמירה'}</button>
        </footer>
      </div>
    </div>
  );
}
